import {
  ACCEPTED_UPLOAD_FORMAT_LABEL,
  isAcceptedUploadImageType,
  isAllowedImageType,
  MAX_PRODUCT_MEDIA_SIZE_BYTES,
} from "@shared/products/photo-constraints";
import { MAX_FILE_SIZE } from "./constants";

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export class PhotoValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PhotoValidationError";
  }
}

function unsupportedTypeError(file: File): ValidationResult {
  return {
    valid: false,
    error: `Unsupported file type: ${file.type || "unknown"}. Allowed: ${ACCEPTED_UPLOAD_FORMAT_LABEL}`,
  };
}

export function validateUploadFileType(file: File): ValidationResult {
  if (!isAcceptedUploadImageType(file.type)) return unsupportedTypeError(file);
  return { valid: true };
}

export function validateStorableFileType(file: File): ValidationResult {
  if (!isAllowedImageType(file.type)) return unsupportedTypeError(file);
  return { valid: true };
}

export function validateFileSize(file: File): ValidationResult {
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = Math.round(file.size / (1024 * 1024));
    return {
      valid: false,
      error: `File too large (${sizeMB}MB). Maximum size is 25MB`,
    };
  }
  return { valid: true };
}

export function validateStorableFileSize(file: File): ValidationResult {
  if (file.size > MAX_PRODUCT_MEDIA_SIZE_BYTES) {
    const sizeMB = (
      Math.ceil((file.size / (1024 * 1024)) * 10) / 10
    ).toFixed(1);
    const limitMB = Math.round(MAX_PRODUCT_MEDIA_SIZE_BYTES / (1024 * 1024));
    return {
      valid: false,
      error: `This photo is ${sizeMB}MB after optimization, over the ${limitMB}MB listing photo limit. Please choose a smaller image.`,
    };
  }
  return { valid: true };
}

export async function checkImageLoadable(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(true);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };

    img.src = url;
  });
}

export async function validateImageFile(file: File): Promise<ValidationResult> {
  const typeResult = validateStorableFileType(file);
  if (!typeResult.valid) {
    return typeResult;
  }

  const sizeResult = validateStorableFileSize(file);
  if (!sizeResult.valid) {
    return sizeResult;
  }

  const loadable = await checkImageLoadable(file);
  if (!loadable) {
    return {
      valid: false,
      error: "Image could not be loaded.",
    };
  }

  return { valid: true };
}
